import { createLoader } from '@meteor/data-hooks';
import { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { switcher } from '../../lib/switcher';
import {
  organizationLoader,
  organizationsLoader,
  useOrganizations,
  useProject
} from '../../organization';
import { lastInstanceIdStore } from '../../stores';
import { withAuth } from '../../user';
import { instanceClient } from '../client';

export let instanceLoader = createLoader({
  name: 'instance',
  parents: [organizationLoader, organizationsLoader],
  fetch: (i: { instanceId: string }) => withAuth(() => instanceClient.instance.get(i)),
  mutators: {
    update: (
      i: {
        name?: string;
      },
      { output: { id } }
    ) =>
      withAuth(() =>
        instanceClient.instance.update({
          ...i,
          instanceId: id
        })
      ),

    delete: (_, { output: { id } }) =>
      withAuth(() =>
        instanceClient.instance.delete({
          instanceId: id
        })
      )
  }
});

export let useInstance = (instanceId: string | null) => {
  let instance = instanceLoader.use(instanceId ? { instanceId } : null);

  return {
    ...instance,
    updateMutator: instance.useMutator('update'),
    deleteMutator: instance.useMutator('delete')
  };
};

export let useCurrentInstance = () => {
  let orgs = useOrganizations();

  let params = useParams<{ instanceId: string }>();
  let [search] = useSearchParams();

  let instanceId = params.instanceId || search.get('instanceId');

  let foundInstance = useMemo(() => {
    if (!instanceId || !orgs.data) return { status: 'loading' as const };

    let org = orgs.data?.find(org =>
      org.projects.some(p => p.instances.some(i => i.id == instanceId || i.slug == instanceId))
    );

    let project = org?.projects.find(p =>
      p.instances.some(i => i.id == instanceId || i.slug == instanceId)
    );

    let instance = project?.instances.find(i => i.id == instanceId || i.slug == instanceId);

    if (!instance) return { status: 'not-found' as const };
    return { status: 'success' as const, instance, organization: org, project };
  }, [orgs.data, instanceId]);

  useEffect(() => {
    if (foundInstance.status != 'success') return;
    lastInstanceIdStore.set(foundInstance.instance.id);
  }, [foundInstance]);

  let res = useInstance(
    switcher({
      loading: null,
      'not-found': instanceId,
      success: () => foundInstance.instance?.id
    })(foundInstance.status)
  );

  return {
    ...res,
    data: res.data
      ? {
          ...res.data,
          project: foundInstance.project!,
          organization: foundInstance.organization!
        }
      : null
  };
};

export let useCurrentProject = () => {
  let instance = useCurrentInstance();
  let project = useProject(instance.data?.project?.id);

  let orgProject = instance.data?.organization.projects.find(
    p => p.id == instance.data?.project?.id
  )!;

  return {
    ...instance,
    data:
      project.data && instance.data && orgProject
        ? {
            ...orgProject,
            ...instance.data.project,
            organization: instance.data.organization
          }
        : null,
    updateMutator: project.updateMutator
    // deleteMutator: project.deleteMutator
  };
};
